import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { MessageSquare, Mail, Phone, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInquiries = () => {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInquiries = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/inquiries`, { headers: { Authorization: `Bearer ${token}` } }); setInquiries(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/inquiries/${id}?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated!'); fetchInquiries();
    } catch (e) { toast.error('Failed'); }
  };

  const getStatusBadge = (status) => {
    const styles = { new: 'bg-primary/10 text-primary', contacted: 'bg-yellow-100 text-yellow-700', converted: 'bg-green-100 text-green-700', closed: 'bg-slate-100 text-slate-700' };
    return styles[status] || '';
  };

  return (
    <AdminLayout title="Inquiries">
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{inquiries.filter(i => i.status === 'new').length}</p><p className="text-sm text-muted-foreground">New</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{inquiries.filter(i => i.status === 'contacted').length}</p><p className="text-sm text-muted-foreground">Contacted</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{inquiries.filter(i => i.status === 'converted').length}</p><p className="text-sm text-muted-foreground">Converted</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{inquiries.length}</p><p className="text-sm text-muted-foreground">Total</p></CardContent></Card>
      </div>

      {loading ? (<div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-24"></CardContent></Card>)}</div>) : inquiries.length > 0 ? (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <Card key={inquiry.id} data-testid={`inquiry-${inquiry.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><MessageSquare className="w-5 h-5 text-primary" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><h4 className="font-semibold">{inquiry.name}</h4><Badge className={getStatusBadge(inquiry.status)}>{inquiry.status}</Badge></div>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{inquiry.email}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{inquiry.phone}</span>
                      {inquiry.course_interest && <Badge variant="outline">{inquiry.course_interest}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{inquiry.message}</p>
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Calendar className="w-3 h-3" />{inquiry.created_at?.split('T')[0]}</p>
                  </div>
                  <Select defaultValue={inquiry.status} onValueChange={(v) => updateStatus(inquiry.id, v)}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['new', 'contacted', 'converted', 'closed'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (<Card><CardContent className="py-16 text-center"><MessageSquare className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No Inquiries</h3></CardContent></Card>)}
    </AdminLayout>
  );
};

export default AdminInquiries;
